// 3rd-party
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

// Components
import Spinner from "../../components/spinner/Spinner";
import IndiaPDFFormDialog from "./IndiaPDFFormDialog";

// Services
import { logout } from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";

// Config
import { appConfig } from "../../config/appConfig";

// AssetComponents
import edit from "../../assets/edit.svg";
import delete1 from "../../assets/delete.svg";
import add from "../../assets/add.svg";
import approve from "../../assets/approve.svg";
import visiable from "../../assets/visiable.svg";

// Styles
import "./IndiaPdf.scss";

type AdminEntry = {
  [x: string]: any;
  id: number;
  pdfName: string;
  pdfUrls: string | string[];
  isActive: boolean;
  status: string;
};

const IndiaPdf = () => {
  const [dataList, setDataList] = useState<AdminEntry[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState<AdminEntry>({
    id: 0,
    pdfName: "",
    pdfUrls: [],
    isActive: false,
    status: "",
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 7;
  const [role, setRole] = useState<string>("");
  const navigate = useNavigate();
  useEffect(() => {
    const storedRole = getStorageItemLocal("role");

    if (storedRole) setRole(JSON.parse(storedRole));
  }, []);
  useEffect(() => {
    const savedData = localStorage.getItem("adminDataList");
    if (savedData) {
      const parsedData: AdminEntry[] = JSON.parse(savedData);
      setDataList(parsedData);
      if (!savedData || parsedData.length === 0) {
        setEditingIndex(null);
      }
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    if (editingIndex !== null) {
      const selectedEntry = dataList.find((item) => item.id === editingIndex);
      if (selectedEntry) {
        const pdfUrls =
          typeof selectedEntry.pdfUrls === "string"
            ? selectedEntry.pdfUrls.split(",")
            : Array.isArray(selectedEntry.pdfUrls)
            ? selectedEntry.pdfUrls
            : [];
        setFormData({
          ...selectedEntry,
          pdfUrls,
        });
      }
    }
  }, [editingIndex, dataList]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(appConfig.serviceUrls.saveIndiaPDFEntries, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        <Button onClick={() => logout(navigate)}>Logout</Button>;
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch data");
      const data: AdminEntry[] = await response.json();
      setDataList(data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: dataList.length + 1, // Increment ID
      pdfName: "",
      pdfUrls: [],
      isActive: false,
      status: "",
    });
    setEditingIndex(null);
  };

  const handleAdd = () => {
    setEditingIndex(null);
    setShowForm(true);
    setFormData({
      id: 0,
      pdfName: "",
      pdfUrls: [],
      isActive: false,
      status: "",
    });
  };

  const handlePublish = async (index: number) => {
    if (!window.confirm("Are you sure you want to publish this entry?")) return;

    const selectedEntry = dataList[index];
    if (!selectedEntry) {
      alert("Invalid entry selected for publishing.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("id", selectedEntry.id.toString());
    formDataToSend.append("status", "Published");
    formDataToSend.append("pdfName", selectedEntry.pdfName);
    formDataToSend.append("isActive", selectedEntry.isActive.toString());
    formDataToSend.append("uploadBy", selectedEntry.uploadBy);
    formDataToSend.append("statusCode", "3");

    const username = getStorageItemLocal("username") ?? "";
    formDataToSend.append("publishBy", username);

    formDataToSend.append("pdfUrls", selectedEntry.pdfUrls.toString());

    const url = `${appConfig.serviceUrls.saveIndiaPDFEntries}/${selectedEntry.id}`;
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(url, {
        method: "PUT",
        body: formDataToSend,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        <Button onClick={() => logout(navigate)}>Logout</Button>;
        return;
      }

      if (!response.ok) throw new Error("Failed to publish entry");

      alert("Entry published successfully.");
      fetchData();
      resetForm();
      setEditingIndex(null);
      setShowForm(false);
    } catch (error) {
      console.error("Error publishing entry:", error);
      alert("Error publishing entry.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (index: number) => {
    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      console.error("Error: dataList is empty");
      return;
    }
    const selectedEntry = dataList[index];
    if (!selectedEntry) {
      console.error("No entry found at index:", index);
      return;
    }
    setFormData({
      ...selectedEntry,
      pdfUrls:
        typeof selectedEntry.pdfUrls === "string"
          ? selectedEntry.pdfUrls.split(",")
          : Array.isArray(selectedEntry.pdfUrls)
          ? selectedEntry.pdfUrls
          : [],
    });
    setEditingIndex(selectedEntry.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure want to delete this entry?")) return;
    setLoading(true);
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(
        `${appConfig.serviceUrls.saveIndiaPDFEntries}/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 401) {
        logout(navigate);
        <Button onClick={() => logout(navigate)}>Logout</Button>;
        return;
      }

      if (!response.ok) throw new Error("Failed to delete");

      fetchData();
      if (editingIndex !== null && dataList[editingIndex]?.id === id) {
        setEditingIndex(null);
      }
    } catch (error) {
      console.error("Error deleting data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="container-wraper">
      <Box
        className="add-wrapper"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          // paddingBottom: "5px",
        }}
      >
        <Typography className="bold-header-text">
          <b>PDF</b>{" "}
        </Typography>
        <Button onClick={handleAdd} size="small" className="custom-button">
          <img src={add} alt="add" className="img-style" />{" "}
          <Typography className="btn-text">Add PDF</Typography>
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 3,
          marginBottom: 2,
        }}
      >
        {loading ? (
          <>
            {" "}
            <Spinner></Spinner>
          </>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              marginTop: 2,
              width: "100%",
            }}
          >
            {dataList?.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow className="custom-table-head-row">
                    {/* <TableCell><b>ID</b></TableCell>           */}
                    <TableCell>
                      <b>PDF Name</b>
                    </TableCell>

                    <TableCell className="nowrap">
                      <b>PDF File</b>
                    </TableCell>

                    <TableCell className="nowrap">
                      <b>Active / Disable</b>
                    </TableCell>
                    <TableCell className="nowrap">
                      <b>Status</b>
                    </TableCell>

                    <TableCell>
                      <b>Actions</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.pdfName}</TableCell>

                        <TableCell>
                          {typeof item.pdfUrls === "string" &&
                          item.pdfUrls.trim() !== "" ? (
                            <Box className="description-wraper">
                              <Typography className="description-txt">
                                View PDF
                              </Typography>

                              <div key={index}>
                                <a
                                  href={item.pdfUrls.trim()}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <IconButton color="primary" size="small">
                                    <img
                                      src={visiable}
                                      alt="visible"
                                      className="img-style"
                                    />
                                  </IconButton>
                                </a>
                              </div>
                              {/* ))} */}
                            </Box>
                          ) : (
                            <Typography>No PDF</Typography>
                          )}
                        </TableCell>

                        <TableCell className="nowrap">
                          <Box className="status-wrapper">
                            <Box
                              className={`status-toggle1 ${
                                item.isActive ? "active" : "Disable"
                              }`}
                            >
                              <Box className="status-circle" />
                            </Box>

                            <Typography className="active-txt" component="span">
                              {item.isActive ? "Active" : "Disable"}
                            </Typography>
                          </Box>
                        </TableCell>
                        {item.status === "Approved" ? (
                          <TableCell className="approved-text">
                            {item.status}
                          </TableCell>
                        ) : item.status === "Published" ? (
                          <TableCell className="approved-text">
                            {item.status}
                          </TableCell>
                        ) : item.status === "Pending" ? (
                          <TableCell className="pending-txt">
                            {item.status}
                          </TableCell>
                        ) : (
                          <TableCell className="publish-txt">
                            {item.status}
                          </TableCell>
                        )}

                        <TableCell
                          align="left"
                          className="nowrap sticky-right-top"
                        >
                          <IconButton
                            color="primary"
                            size="small"
                            className="Icon-button"
                            onClick={() => {
                              handleEdit(page * rowsPerPage + index);
                            }}
                          >
                            <img
                              src={edit}
                              alt="visiable"
                              className="img-style"
                            />
                          </IconButton>

                          {role === "System Admin" && (
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleDelete(item.id)}
                            >
                              <img
                                src={delete1}
                                alt="visiable"
                                className="img-style"
                              />
                            </IconButton>
                          )}

                          {item.status === "Approved" && (
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() =>
                                handlePublish(page * rowsPerPage + index)
                              }
                            >
                              <img
                                src={approve}
                                alt="visiable"
                                className="img-style"
                              />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "1rem",
                }}
              >
                <Typography>No Data Found</Typography>
              </Box>
            )}
          </TableContainer>
        )}
      </Box>{" "}
      <IndiaPDFFormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        fetchData={fetchData}
        resetForm={resetForm}
        formData={formData}
        setFormData={setFormData}
        editingIndex={editingIndex}
        setShowForm={setShowForm}
        setEditingIndex={setEditingIndex}
        dataList={dataList}
      />
    </Box>
  );
};

export default IndiaPdf;
